import * as React from 'react';
import { ReactSVG } from 'react-svg';
import styled, { css } from 'styled-components';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import CloudUpload from '@material-ui/icons/CloudUpload';

import { ButtonMain } from '../../atoms';
import { Cropper, ModalDefaultDraggable } from '../../molecules';
import {
  requestFailure,
  setActiveRequest,
  setUserInfo,
  userInfoSelector,
} from '../../redux/ducks/userAuth';
import apiUserSettings from '../../api/userSettings';

import ArrowLeftSVG from '../../resources/icons/arrowLeft.svg';
import ArrowRightSVG from '../../resources/icons/arrowRight.svg';
import PersonSVG from '../../resources/icons/person.svg';

const getNewAngle = (
  oldAngle: number,
  direction: Direction,
) => {
  const step = 90 * direction;

  if (Math.abs(oldAngle) === 270) return 0;
  return oldAngle + step;
};

const allowedFileSize = 2e6;

const AvatarModal = ({
  onClose,
  setIsSuccessAvatarUploadModal,
  setIsAvatarModalOpen,
}: Props) => {
  const dispatch = useDispatch();
  const { id } = useSelector(userInfoSelector);

  const cropperRef = useRef(null);
  const [selectedImage, setSelectedImage] = useState<
    string | ArrayBuffer
  >('');
  const [rotationAngle, setRotationAngle] = useState(0);
  const [wrapperHeight, setWrapperHeight] = useState(null);

  const [isImageTooBig, setIsImageTooBig] = useState(false);

  const [isUploadError, setIsUploadError] = useState(false);
  const [dragging, setDragging] = useState(false);

  useEffect(() => {
    if (cropperRef.current) {
      cropperRef.current.setCanvasData({ top: 0 });
      cropperRef.current.cropper.resize();
    }
  }, [wrapperHeight]);

  const updateWrapperDimensions = newAngle => {
    const { width } = cropperRef.current.getCanvasData();

    const isVerticalMode = !!((newAngle / 90) % 2);

    setWrapperHeight(isVerticalMode ? width : null);
  };

  const rotate = (direction: Direction) => {
    const newAngle = getNewAngle(rotationAngle, direction);
    setRotationAngle(newAngle);
    updateWrapperDimensions(newAngle);
  };

  const rotateLeft = () => {
    rotate(-1);
  };

  const rotateRight = () => {
    rotate(1);
  };

  const consumeFile = (file: File | undefined) => {
    if (!file) return;
    if (file.size > allowedFileSize) {
      setIsImageTooBig(true);
      return;
    }
    setIsImageTooBig(false);
    const reader = new FileReader();
    reader.addEventListener('load', () => {
      setSelectedImage(reader.result);
    });
    reader.readAsDataURL(file);
  };

  const handleAvatarChange = e => {
    consumeFile(e.target.files?.[0]);
  };

  const handleDragOver = e => {
    e.preventDefault();
    setDragging(true);
  };
  const handleDragLeave = e => {
    e.preventDefault();
    setDragging(false);
  };
  const handleDrop = e => {
    e.preventDefault();
    setDragging(false);
    consumeFile(e.dataTransfer.files?.[0]);
  };

  const uploadAvatar = async (newAvatar: Blob) => {
    try {
      dispatch(setActiveRequest());

      const formData = new FormData();

      formData.set('avatar', newAvatar, 'avatar.jpeg');

      const newUserInfo = await apiUserSettings.sendNewUserAvatar(
        id,
        formData,
      );

      const { avatar, ...restInfo } = newUserInfo;

      // we need to do so, because user avatar always updated under the same url
      const newAvatarLink = avatar
        ? `${avatar}?${Date.now()}`
        : avatar;

      dispatch(
        setUserInfo({
          ...restInfo,
          avatar: newAvatarLink,
        }),
      );

      setIsAvatarModalOpen(false);
      setIsSuccessAvatarUploadModal(true);
    } catch (e) {
      setIsUploadError(true);

      if (e.status === 400) {
        dispatch(requestFailure(e.response.data));
      }
    }
  };

  const handleSave = async () => {
    cropperRef.current
      .getCroppedCanvas()
      .toBlob(uploadAvatar, 'image/jpeg', 0.85);
  };

  const handleClose = () => {
    setSelectedImage('');
    onClose();
  };

  return (
    <ModalDefaultDraggable title="Upload profile photo" onClose={handleClose} narrow centered>
      <ContentWrapper>
        {!selectedImage && (
          <>
            <FileInputLabel
              active={dragging}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <FileInput
                type="file"
                accept="image/*"
                onChange={handleAvatarChange}
                multiple={false}
              />

              <DropIcon>
                <CloudUpload />
              </DropIcon>
              <DropPrimary>
                {dragging ? 'Drop photo to upload' : 'Drag a profile photo here'}
              </DropPrimary>
              <DropSecondary>or click below to browse</DropSecondary>

              <AvatarPlaceholder />

              <ButtonMainStyled as="div">
                Select a photo from your computer
              </ButtonMainStyled>
              <FileHint>JPG or PNG, max 2MB</FileHint>
            </FileInputLabel>

            {isImageTooBig && (
              <ErrorUploadText>
                The size of the avatar should be less than 2MB. Please choose a smaller file.
              </ErrorUploadText>
            )}
          </>
        )}
        {selectedImage && (
          <>
            <BlockTitle>
              To crop this image, drag the region below
            </BlockTitle>

            <CropperStyled
              ref={cropperRef}
              src={selectedImage}
              viewMode={1}
              aspectRatio={1}
              guides={false}
              rotateTo={rotationAngle}
              autoCropArea={1}
              zoomable={false}
              toggleDragModeOnDblclick={false}
              background={false}
              height={wrapperHeight}
            />

            <RotationButtons>
              <RotationButton onClick={rotateLeft}>
                <ArrowIcon src={ArrowLeftSVG} />
                <span>left</span>
              </RotationButton>
              <RotationButton onClick={rotateRight}>
                <ArrowIcon src={ArrowRightSVG} />
                <span>right</span>
              </RotationButton>
            </RotationButtons>

            <ButtonMainStyled
              onClick={handleSave}
              disabled={!cropperRef}
            >
              Set as profile photo
            </ButtonMainStyled>
            {isUploadError && (
              <ErrorUploadText>
                Something went wrong. The size of the avatar
                should be less than 2Mb, this file is too
                big.
              </ErrorUploadText>
            )}
          </>
        )}
      </ContentWrapper>
    </ModalDefaultDraggable>
  );
};

const ContentWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  position: relative;
`;

const BlockTitle = styled.div`
  text-align: center;
  margin-bottom: 20px;
  font-size: 1.125rem;
  font-weight: 700;
  color: var(--mainText);
`;

const RotationButtons = styled.div`
  display: flex;
  align-items: center;
  width: 100px;
  justify-content: space-between;
  margin: 0 auto 24px;
`;

const RotationButton = styled.button.attrs({
  type: 'button',
})`
  display: flex;
  flex-direction: column;
  align-items: center;
  color: var(--secondaryText);
  letter-spacing: 0.15px;

  span::first-letter {
    text-transform: uppercase;
  }
`;

const CropperStyled = styled(Cropper)`
  width: 100%;
  height: ${({ height }) =>
    height ? `${height}px` : '145px'};
  margin-bottom: 24px;

  .cropper-container {
    ${({ height }) =>
      height &&
      css`
        height: ${`${height}px`} !important;
      `}
  }

  .point-n,
  .point-w,
  .point-e,
  .point-s {
    display: none;
  }
`;

const ButtonMainStyled = styled(ButtonMain)`
  text-align: center;
  font-weight: 700;
  width: 100%;
  max-width: 315px;
  position: relative;
  cursor: pointer;
  z-index: var(--initialZIndex);
  padding-top: 11px;
  padding-bottom: 11px;
  margin: 0 auto;
`;

const ArrowIcon = styled(ReactSVG)`
  color: var(--primary);
  margin-bottom: 5px;

  svg {
    width: 25px;
    height: 16px;
  }
`;

const FileInputLabel = styled.label<{ active?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  width: 100%;
  padding: 28px 20px 24px;
  border: 2px dashed var(--border);
  border-radius: 12px;
  background: var(--neutral-0);
  cursor: pointer;
  transition: border-color 0.15s ease, background-color 0.15s ease;

  &:hover {
    border-color: var(--primary-300);
    background: var(--primary-50);
  }

  ${({ active }) =>
    active &&
    css`
      border-color: var(--primary-600);
      background: var(--primary-50);
    `}
`;

const FileInput = styled.input.attrs({
  type: 'file',
  accept: 'image/*',
  multiple: false,
})`
  display: block;
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;
`;

const DropIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: var(--primary-50);
  color: var(--primary-600);
  margin-bottom: 12px;

  svg {
    font-size: 1.75rem;
  }
`;

const DropPrimary = styled.div`
  font-size: 0.9375rem;
  font-weight: 600;
  color: var(--mainText);
  margin-bottom: 4px;
  text-align: center;
`;

const DropSecondary = styled.div`
  font-size: 0.8125rem;
  color: var(--neutral-500);
  margin-bottom: 16px;
  text-align: center;
`;

const FileHint = styled.div`
  font-size: 0.75rem;
  color: var(--neutral-500);
  margin-top: 12px;
`;

const AvatarPlaceholder = styled(ReactSVG).attrs({
  src: PersonSVG,
})`
  background: var(--neutral-100);
  display: flex;
  padding: 18px 22px;
  color: var(--neutral-400);
  margin-bottom: 18px;
  border-radius: 10px;

  svg {
    width: 40px;
    height: 50px;
  }
`;

const ErrorUploadText = styled.span`
  display: block;
  text-align: center;
  color: var(--danger-500);
  font-size: 0.8125rem;
  margin-top: 12px;
`;

interface Props {
  onClose: () => void;
  setIsSuccessAvatarUploadModal: (arg: boolean) => void;
  setIsAvatarModalOpen: (arg: boolean) => void;
}

type Direction = 1 | -1;

export default AvatarModal;
